import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';
import Avatar from './Avatar';

/**
 * Sprint 7 — PeoplePicker (combobox accessible avec recherche).
 *
 * Implémentation ARIA combobox sans dépendance externe :
 *  - input texte avec recherche debouncée (200ms)
 *  - dropdown listbox visible quand input focus
 *  - navigation clavier ↑/↓/Home/End/Enter/Échap
 *  - aria-activedescendant pour annoncer l'option highlighted au lecteur d'écran
 *
 * Props :
 *  - fetchOptions  : (q: string) => Promise<Array<{ id, fullName, initials?,
 *                    avatarUrl?, jobTitle?, roleName?, departmentName? }>>
 *  - onSelect      : (option) => void
 *  - placeholder   : texte input
 *  - autoFocus     : focus à l'ouverture
 *  - emptyMessage  : message "0 résultat"
 *  - disabled      : input désactivé
 */
export default function PeoplePicker({
  fetchOptions,
  onSelect,
  placeholder = 'Rechercher…',
  autoFocus = false,
  emptyMessage = 'Aucun résultat',
  disabled = false,
}) {
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);
  const listId = useId();
  const optionIdPrefix = useId();

  // Debounce 200ms : pas de fetch sur chaque frappe.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query), 200);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.resolve(fetchOptions(debounced))
      .then((items) => {
        if (cancelled) return;
        setOptions(Array.isArray(items) ? items : items?.items ?? []);
        setHighlight(0);
      })
      .catch(() => {
        if (cancelled) return;
        setOptions([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debounced, fetchOptions]);

  function commitOption(opt) {
    if (!opt) return;
    onSelect?.(opt);
    setQuery('');
    setDebounced('');
    setOpen(false);
  }

  function onKeyDown(e) {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setOpen(true);
        e.preventDefault();
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, Math.max(0, options.length - 1)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Home') {
      e.preventDefault();
      setHighlight(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      setHighlight(Math.max(0, options.length - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      commitOption(options[highlight]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  const activeId = useMemo(
    () => (options[highlight]?.id ? `${optionIdPrefix}-${options[highlight].id}` : undefined),
    [options, highlight, optionIdPrefix],
  );

  return (
    <div style={{ position: 'relative' }}>
      <div
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-owns={listId}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '6px 10px',
          border: '1px solid var(--glass-border)',
          borderRadius: 'var(--radius-sm)',
          background: 'var(--bg)',
        }}
      >
        <Search size={14} style={{ color: 'var(--text-muted)' }} />
        <input
          ref={inputRef}
          type="text"
          value={query}
          autoFocus={autoFocus}
          disabled={disabled}
          placeholder={placeholder}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onKeyDown={onKeyDown}
          aria-autocomplete="list"
          aria-controls={listId}
          aria-activedescendant={activeId}
          style={{
            flex: 1,
            background: 'transparent',
            color: 'var(--text)',
            border: 'none',
            outline: 'none',
            fontSize: 14,
          }}
        />
        {query && (
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              setQuery('');
              setDebounced('');
              inputRef.current?.focus();
            }}
            title="Effacer"
            aria-label="Effacer"
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--text-muted)',
              cursor: 'pointer',
              padding: 2,
            }}
          >
            <X size={14} />
          </button>
        )}
      </div>

      {open && (
        <ul
          id={listId}
          role="listbox"
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            zIndex: 1100,
            margin: 0,
            padding: 4,
            listStyle: 'none',
            background: 'var(--card-bg)',
            border: '1px solid var(--glass-border)',
            borderRadius: 'var(--radius-sm)',
            boxShadow: 'var(--shadow-card)',
            maxHeight: 320,
            overflowY: 'auto',
          }}
        >
          {loading && options.length === 0 && (
            <li
              style={{
                padding: '8px 10px',
                color: 'var(--text-muted)',
                fontSize: 13,
              }}
            >
              Chargement…
            </li>
          )}
          {!loading && options.length === 0 && (
            <li
              style={{
                padding: '8px 10px',
                color: 'var(--text-muted)',
                fontSize: 13,
              }}
            >
              {emptyMessage}
            </li>
          )}
          {options.map((opt, idx) => {
            const isHighlighted = idx === highlight;
            return (
              <li
                key={opt.id}
                id={`${optionIdPrefix}-${opt.id}`}
                role="option"
                aria-selected={isHighlighted}
                onMouseEnter={() => setHighlight(idx)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  commitOption(opt);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '6px 8px',
                  borderRadius: 'var(--radius-sm)',
                  background: isHighlighted ? 'var(--glass)' : 'transparent',
                  cursor: 'pointer',
                }}
              >
                <Avatar person={opt} size={28} />
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    minWidth: 0,
                    flex: 1,
                  }}
                >
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: 500,
                      color: 'var(--text)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {opt.fullName}
                  </span>
                  {(opt.roleName || opt.jobTitle || opt.departmentName) && (
                    <span
                      style={{
                        fontSize: 12,
                        color: 'var(--text-muted)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {[opt.roleName ?? opt.jobTitle, opt.departmentName]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
